import { $contentful } from '@/boot/contentful'

const META_DEFAULT = {
  title: '',
  meta: {
    description: { name: 'description', content: 'Welcome to Peg!' },
    robots: { name: 'robots', content: 'index,follow' },
  },
  link: {},
  path: '',
}

export const usePageMetaStore = defineStore('pageMeta', () => {
  const pageMeta = ref({ ...META_DEFAULT })
  const route = useRoute()

  function pageMetaReset() {
    pageMeta.value = { ...META_DEFAULT }
  }

  function pageMetaBuild(params = {}) {
    // reset meta on route change
    if (pageMeta.value.path !== route.path) {
      pageMetaReset()
      // set new path
      pageMeta.value = { ...pageMeta.value, path: route.path }
    }

    if (process.env.CLIENT) {
      // $logger.debug('PAGE META PARAMS', params)
    }

    const { contentfulMeta = [], title, description } = params
    // prefer params over contentful
    if (title) {
      pageMetaSet({ title })
    }

    if (description) {
      pageMetaSet({ description })
    }

    // still apply contentful meta if defined, but without overriding provided params
    if (contentfulMeta.length && !title && !description) {
      contentfulMeta.forEach(({ name, content }) => {
        if (name === 'title') {
          pageMetaSet({ title: content })
        } else if (name === 'description') {
          pageMetaSet({ description: content })
        } else if (name === 'canonical') {
          pageMetaSet({ canonical: content })
        } else {
          // Add or replace meta property
          const meta = { ...pageMeta.value.meta }
          if (name.startsWith('og:')) {
            meta[name] = { property: name, content }
          } else {
            meta[name] = { name, content }
          }
          Object.assign(pageMeta.value, { meta })
        }
      })
    }

    if (Object.keys(pageMeta.value.meta).length) {
      // ensure robots meta is set
      const meta = { ...pageMeta.value.meta }
      if (meta.robots && !meta.robots.content) {
        meta.robots.content = 'index,follow'
      }
      Object.assign(pageMeta.value, { meta })
    }

    // if canonical not defined, self-canonical with no query params
    if (!pageMeta.value.link.canonical) {
      // add trailing slash if not present
      const slashPath = /^.*?\/$/.test(route.path) ? route.path : `${route.path}/`
      const link = { ...pageMeta.value.link }
      link.canonical = {
        rel: 'canonical',
        href: `https://${window.location.host}${slashPath}`,
      }
      Object.assign(pageMeta.value, { link })
    }

    if (process.env.CLIENT) {
      // $logger.debug('PAGE META BUILD', pageMeta.value)
    }
  }

  function pageMetaSet({ title, description, robots, canonical }) {
    let { meta, link } = pageMeta.value

    if (title) {
      const replacedTitle = $contentful.replaceContentTokens(title)
      pageMeta.value = { ...pageMeta.value, title: replacedTitle }
      // Set og:title
      meta['og:title'] = { property: 'og:title', content: replacedTitle }
    }

    if (description) {
      const content = $contentful.replaceContentTokens(description)
      meta.description = { name: 'description', content }
      meta['og:description'] = { property: 'og:description', content }
    }

    if (robots) {
      meta.robots = { name: 'robots', content: robots }
    }

    if (canonical) {
      link.canonical = { rel: 'canonical', href: canonical }
    }

    Object.assign(pageMeta.value, { meta, link })

    if (process.env.CLIENT) {
      // $logger.debug('PAGE META SET', pageMeta.value)
    }
  }

  return {
    pageMeta,
    pageMetaReset,
    pageMetaBuild,
    pageMetaSet,
  }
})
